// @ts-ignore
import { createSlice } from '@reduxjs/toolkit';
import {
  CheckAuth,
  CreateSession,
  createNVFSession,
  createNVFSessionSMS,
  createVFSession,
  createVFSessionSMS,
  sendNVFOTP,
  sendVFOTP,
} from '@/services/session-service';
import { getSessionStorage, setSessionStorage } from '@/utils';
import {
  getAddressData,
  getApplicationAddress,
  getPersonalInfo,
  getSiteInfoList,
  getDataInsights,
  insertFixedApplication,
  queryFixedInfrastructure,
  queryFixTariffs,
  saveAddressInfo,
  saveFixedTariff,
  updatePersonalInfo,
  insertFixedColdLead,
} from '@/services/fixed-services';
import { IInitialState } from '@/redux/types';

const initialState: IInitialState = {
  sid: '',
  phone: '',
  isError: false,
  nextStep: '',
  errorMessage: '',
  loading: false,
  tariffPageLoading: false,
  segment: false,
  appId: '',
  fixTariffQuery: false,
  tariffsAllData: [],
  applicationAddress: {},
  autoLogin: false,
  appWebview: false,
  userInformation: {},
  dataToken: '',
  selectedAddressPopulation: {},
  couponCodeSuccess: false,
  couponCode: '',
  couponCodeSuccessDesc: '',
  tarifeErrorMessage: '',
  OTPCode: '',
  tbgClusterId: '',
  infrastructureTypes: '',
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    resetStore: () => initialState,
    addErrorMessage: (state, action) => {
      state.isError = true;
      state.errorMessage = action.payload;
    },
    clearErrorMessage: (state: any) => {
      state.isError = false;
      state.errorMessage = '';
    },
    setPhone: (state, action) => {
      state.phone = action.payload?.replace(/[^\d]/g, '');
    },
    setOTPCode: (state, action) => {
      state.OTPCode = action.payload?.join('');
    },
    clearAllState: (state) => {
      state.sid = '';
      state.isError = false;
      state.errorMessage = '';
      setSessionStorage('userData', {});
    },
    setfixTariffQuery: (state, action) => {
      state.fixTariffQuery = action.payload;
    },
    setAutoLogin: (state, action) => {
      state.autoLogin = action.payload;
    },
    setAppWebview: (state, action) => {
      state.appWebview = action.payload;
    },
    setTbgClusterId: (state, action) => {
      state.tbgClusterId = action.payload;
    },
    setUserInformation: (state, action) => {
      state.userInformation = {
        ...state.userInformation,
        [action.payload.arr]: action.payload.value,
      };
    },
    setSelectedAddressPopulation: (state, action) => {
      state.selectedAddressPopulation = action.payload;
    },
    setCouponCodeSuccess: (state, action) => {
      state.couponCodeSuccess = action.payload;
    },
    setCouponCode: (state, action) => {
      state.couponCode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendNVFOTP.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(sendNVFOTP.fulfilled, (state, action) => {
      state.loading = false;
      const res = action.payload;
      if (res && res.result && res.result.result === 'SUCCESS') {
        state.sid = action.payload.nvfOtp.sid;
      } else if (
        res &&
        res.result &&
        res.result.result === 'FAIL' &&
        res.result.resultCode !== 'S9703100000'
      ) {
        state.isError = true;
        state.errorMessage = res?.result?.resultDesc;
      }
    });
    builder.addCase(sendNVFOTP.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(sendVFOTP.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(sendVFOTP.fulfilled, (state, action) => {
      state.loading = false;
      const res = action.payload;
      if (res && res.result && res.result.result === 'SUCCESS') {
        state.sid = res?.sessionOtp?.sid;
      } else {
        state.errorMessage = res?.result?.resultDesc;
        state.isError = true;
      }
    });
    builder.addCase(sendVFOTP.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(createNVFSession.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(createNVFSession.fulfilled, (state, action) => {
      state.loading = false;
      const res = action.payload;
      if (res && res.result && res.result.result === 'SUCCESS') {
        state.sid = res.nvfSession.sid;
        const storedData = getSessionStorage('userData');
        storedData.phone = state.phone;
        storedData.sid = state.sid;
        setSessionStorage('userData', storedData);
      } else {
        state.isError = true;
        state.errorMessage = res.result.resultDesc;
      }
    });
    builder.addCase(createNVFSession.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(createNVFSessionSMS.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(createNVFSessionSMS.fulfilled, (state, action) => {
      state.loading = false;
      const res = action.payload;
      if (res && res.result && res.result.result === 'SUCCESS') {
        state.sid = res?.nvfSession?.sid;
        const storedData = getSessionStorage('userData') || ({} as any);
        storedData.phone = state.phone;
        storedData.sid = state.sid;
        storedData.tbg = res?.nvfSession?.segment?.clusterId;
        state.tbgClusterId = res?.nvfSession?.segment?.clusterId;
        setSessionStorage('userData', storedData);
      } else {
        state.isError = true;
        state.errorMessage = res.result.resultDesc;
      }
    });
    builder.addCase(createNVFSessionSMS.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(createVFSession.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(createVFSession.fulfilled, (state, action) => {
      state.loading = false;
      const res = action.payload;

      if (res && res.result && res.result.result === 'SUCCESS') {
        state.sid = res.session.sessionId;
        state.segment = res?.subscriber?.segmentList?.includes('POSTPAID');
        state.tbgClusterId = res?.subscriber?.clusterId;
        const storedData = getSessionStorage('userData');
        storedData.phone = state.phone;
        storedData.sid = state.sid;
        storedData.segment = state.segment;
        storedData.clusterId = res?.subscriber.clusterId;
        setSessionStorage('userData', storedData);
      } else {
        state.isError = true;
        state.errorMessage = res.result.resultDesc;
      }
    });
    builder.addCase(createVFSession.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(createVFSessionSMS.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(createVFSessionSMS.fulfilled, (state, action) => {
      state.loading = false;
      const res = action.payload;

      if (res && res.result && res.result.result === 'SUCCESS') {
        state.sid = res.session.sessionId;
        state.segment = res?.subscriber?.segmentList?.includes('POSTPAID');
        const storedData = getSessionStorage('userData') || ({} as any);
        storedData.phone = state.phone;
        storedData.sid = state.sid;
        storedData.segment = state.segment;
        setSessionStorage('userData', storedData);
      } else {
        state.isError = true;
        state.errorMessage = res.result.resultDesc;
      }
    });
    builder.addCase(createVFSessionSMS.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(insertFixedApplication.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(insertFixedApplication.fulfilled, (state, action) => {
      state.loading = false;
      if (
        action.payload &&
        action.payload.result &&
        action.payload.result.result === 'SUCCESS'
      ) {
        state.appId = action.payload.applicationId;
        const storedData = getSessionStorage('userData');
        storedData.appId = state.appId;
        setSessionStorage('userData', storedData);
      } else if (action?.payload?.result?.result == 'FAIL') {
        if (action?.payload?.result?.resultCode == 'S9751300000') {
          state.tarifeErrorMessage =
            'Üzgünüz, girdiğiniz adreste ev interneti altyapısı bulunamadı. Girdiğiniz adresi düzenleyebilir ya da başka adres sorgulayabilirsiniz.';
        } else {
          state.tarifeErrorMessage = action?.payload?.result?.resultDesc;
        }
      }
    });
    builder.addCase(insertFixedApplication.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getApplicationAddress.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getApplicationAddress.fulfilled, (state, action) => {
      state.loading = false;
      if (
        action.payload &&
        action.payload.result &&
        action.payload.result.result === 'SUCCESS'
      ) {
        state.applicationAddress = action.payload.applicationAddress;
        const storedData = getSessionStorage('userData');
        storedData.applicationAddress = state.applicationAddress;
        setSessionStorage('userData', storedData);
      } else if (action?.payload?.result?.result == 'FAIL') {
        if (action?.payload?.result?.resultCode == 'S9751300000') {
          state.tarifeErrorMessage =
            'Üzgünüz, girdiğiniz adreste ev interneti altyapısı bulunamadı. Girdiğiniz adresi düzenleyebilir ya da başka adres sorgulayabilirsiniz.';
        } else {
          state.tarifeErrorMessage = action?.payload?.result?.resultDesc;
        }
      }
    });
    builder.addCase(getApplicationAddress.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getAddressData.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getAddressData.fulfilled, (state, action) => {
      if (action?.payload?.result?.result == 'FAIL') {
        if (action?.payload?.result?.resultCode == 'S9751300000') {
          state.tarifeErrorMessage =
            'Üzgünüz, girdiğiniz adreste ev interneti altyapısı bulunamadı. Girdiğiniz adresi düzenleyebilir ya da başka adres sorgulayabilirsiniz.';
        } else {
          state.tarifeErrorMessage = action?.payload?.result?.resultDesc;
        }
      }
      state.loading = false;
    });
    builder.addCase(getAddressData.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getSiteInfoList.pending, (state, action) => {
      state.tariffPageLoading = true;
    });
    builder.addCase(getSiteInfoList.fulfilled, (state, action) => {
      state.tariffPageLoading = false;
    });
    builder.addCase(getSiteInfoList.rejected, (state, action) => {
      state.tariffPageLoading = false;
    });
    builder.addCase(getDataInsights.pending, (state, action) => {
      state.tariffPageLoading = true;
    });
    builder.addCase(getDataInsights.fulfilled, (state, action) => {
      state.dataToken = action?.payload?.data;
      state.tariffPageLoading = false;
    });
    builder.addCase(getDataInsights.rejected, (state, action) => {
      state.tariffPageLoading = false;
    });
    builder.addCase(queryFixedInfrastructure.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(queryFixedInfrastructure.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(queryFixedInfrastructure.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(queryFixTariffs.pending, (state, action) => {
      state.tariffPageLoading = true;
    });
    builder.addCase(queryFixTariffs.fulfilled, (state, action) => {
      state.tariffPageLoading = false;
      if (action?.payload?.result?.result == 'SUCCESS') {
        state.couponCodeSuccessDesc =
          action?.payload?.linkAtModel?.result?.resultDesc;
        state.tariffsAllData =
          action?.payload?.tariffGroupList?.tariffGroup?.flatMap((group: any) =>
            group.tariffList.map((tariff: any) => {
              return {
                id: tariff.id,
                name: tariff.name,
                downloadLabel: tariff.downloadLabel,
                downloadSubLabel: tariff.downloadSubLabel,
                firstTermLabel: tariff.firstTermLabel,
                lastTermLabel: tariff.lastTermLabel,
                priceFirstYear: tariff.priceFirstYear,
                priceSecondYear: tariff.priceSecondYear,
                tag: tariff.tag,
                bulletList: tariff.bulletList,
                detailDesc: tariff.detailDesc,
                akn: tariff.akn,
                isYouth: tariff.isYouth,
                dsmartCampaign: tariff.dsmartCampaign,
                isDigitalTariff: tariff.isDigitalTariff,
                promoAvailable: tariff.promoAvailable,
                promoFirstTermLabel: tariff.promoFirstTermLabel,
                promoLastTermLabel: tariff.promoLastTermLabel,
                downloadSpeedPeriod: tariff.downloadSpeedPeriod,
                benefitMaxSpeed: tariff.benefitMaxSpeed,
                connectionType: tariff.connectionType,
                additionalBenefits: tariff.additionalBenefits,
                modemPrice: tariff.modemPrice,
                infrastructureName: tariff.infrastructureName,
                flags: tariff.flags,
                hiddenOffer: tariff.hiddenOffer,
                hiddenTag: tariff.hiddenTag,
                infrastructureTypes: tariff.infrastructureTypes,
                countDown: tariff.countDown,
                countDownEndDate: tariff.countDownEndDate,
                campaignName: tariff.campaignName,
                discountedPriceEnabled: tariff.discountedPriceEnabled,
                discountedFirstPrice: tariff.discountedFirstPrice,
                discountedSecondPrice: tariff.discountedSecondPrice,
                linkAtEnabled: tariff.linkAtEnabled,
                tariffHeaderIcon: tariff.tariffHeaderIcon,
                tariffHeaderColorCode: tariff.tariffHeaderColorCode,
                packageType: tariff.packageType,
                hbCheckLinkatEnabled: tariff.hbCheckLinkatEnabled,
              };
            })
          );
      } else if (action?.payload?.result?.result == 'FAIL') {
        if (action?.payload?.result?.resultCode == 'S9751300000') {
          state.tarifeErrorMessage =
            'Üzgünüz, girdiğiniz adreste ev interneti altyapısı bulunamadı. Girdiğiniz adresi düzenleyebilir ya da başka adres sorgulayabilirsiniz.';
        } else {
          state.tarifeErrorMessage = action?.payload?.result?.resultDesc;
        }
      }
    });
    builder.addCase(queryFixTariffs.rejected, (state, action) => {
      state.tariffPageLoading = false;
    });
    builder.addCase(saveAddressInfo.pending, (state, action) => {
      state.tariffPageLoading = true;
    });
    builder.addCase(saveAddressInfo.fulfilled, (state, action) => {
      if (
        action &&
        action.payload &&
        action.payload.result &&
        action.payload.result.result === 'SUCCESS'
      ) {
        setSessionStorage(
          'infrastructureTypes',
          action?.payload?.infrastructureTypes || 'no infra'
        );
        state.infrastructureTypes =
          action?.payload?.infrastructureTypes || 'no infra';
      } else if (
        action &&
        action.payload &&
        action.payload.result &&
        action.payload.result.result === 'FAIL'
      ) {
        if (action?.payload?.result?.resultCode == 'S9751300000') {
          setSessionStorage(
            'infrastructureTypes',
            action?.payload?.infrastructureTypes || 'no infra'
          );
          state.infrastructureTypes =
            action?.payload?.infrastructureTypes || 'no infra';

          state.tarifeErrorMessage =
            'Üzgünüz, girdiğiniz adreste ev interneti altyapısı bulunamadı. Girdiğiniz adresi düzenleyebilir ya da başka adres sorgulayabilirsiniz.';
        } else {
          setSessionStorage(
            'infrastructureTypes',
            action?.payload?.infrastructureTypes || 'no infra'
          );
          state.infrastructureTypes =
            action?.payload?.infrastructureTypes || 'no infra';
          state.tarifeErrorMessage = action?.payload?.result?.resultDesc;
        }
      }
      state.tariffPageLoading = false;
    });
    builder.addCase(saveAddressInfo.rejected, (state, action) => {
      state.tariffPageLoading = false;
    });
    builder.addCase(saveFixedTariff.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(saveFixedTariff.fulfilled, (state, action) => {
      if (
        action &&
        action.payload &&
        action.payload.result &&
        action.payload.result.result === 'FAIL'
      ) {
      }
      if (action?.payload?.result?.resultCode == 'S9751300000') {
        state.tarifeErrorMessage =
          'Üzgünüz, girdiğiniz adreste ev interneti altyapısı bulunamadı. Girdiğiniz adresi düzenleyebilir ya da başka adres sorgulayabilirsiniz.';
      } else {
        state.tarifeErrorMessage = action?.payload?.result?.resultDesc;
      }
      state.loading = false;
    });
    builder.addCase(saveFixedTariff.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getPersonalInfo.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getPersonalInfo.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getPersonalInfo.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updatePersonalInfo.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updatePersonalInfo.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updatePersonalInfo.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(CreateSession.fulfilled, (state, action) => {
      state.loading = false;
      const res = action.payload;
      if (res && res.result && res.result.result === 'SUCCESS') {
        state.sid = res.session.sessionId;
        state.segment = res.subscriber.segmentList.includes('POSTPAID');
        state.phone = res.subscriber.msisdn;
        const storedData = getSessionStorage('userData');
        storedData.phone = state.phone;
        storedData.sid = state.sid;
        storedData.formName = 'FULL_FORM_AUTO_LOGIN';
        storedData.segment = state.segment;
        state.tbgClusterId = res?.subscriber?.clusterId;
        setSessionStorage('userData', storedData);
      } else {
        state.isError = true;
      }
    });
    builder.addCase(CreateSession.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(CreateSession.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(CheckAuth.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(CheckAuth.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(CheckAuth.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(insertFixedColdLead.fulfilled, (state, action) => {});
    builder.addCase(insertFixedColdLead.pending, (state, action) => {});
    builder.addCase(insertFixedColdLead.rejected, (state, action) => {});
  },
});

export const {
  addErrorMessage,
  clearErrorMessage,
  setPhone,
  clearAllState,
  setfixTariffQuery,
  setUserInformation,
  setSelectedAddressPopulation,
  setCouponCodeSuccess,
  setCouponCode,
  resetStore,
  setTbgClusterId,
} = appSlice.actions;
export default appSlice.reducer;
